import { useDispatch } from 'react-redux';
import { FileRejection } from 'react-dropzone';

import Chip, { Color, Width } from 'lib/Chip';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import Button, { Color as ButtonColor } from 'lib/Button';
import FileUpload from 'lib/FileUpload';
import { NotificationMessages } from 'consts/notifications';
import { ACCEPTED_FILE_TYPE_XLS_ONLY } from 'consts/fileType';
import { FILE_SIZE_20MB } from 'modules/api/constants';
import { addToastNotification } from 'store/notification/reducer';
import { FileList } from 'components/common/types';

interface VisualUnitFilesUploadProps {
  isReadOnly: boolean;
  visualUnitFiles: FileList[] | undefined;
  onChangeVisualUnitFiles: (files: FileList[]) => void;
}

const VisualUnitFilesUpload = ({
  isReadOnly,
  visualUnitFiles,
  onChangeVisualUnitFiles,
}: VisualUnitFilesUploadProps): React.ReactElement => {
  const dispatch = useDispatch();

  const onFileChange = (acceptedFiles: File[], rejectedFiles: FileRejection[]): void => {
    if (rejectedFiles?.length) {
      dispatch(
        addToastNotification({ description: NotificationMessages.INVALID_UPLOAD, type: 'Warning', timeout: 10000 }),
      );
      return;
    }

    const isDuplicateFile = acceptedFiles.find((file) => visualUnitFiles?.find((item) => item.name === file.name));
    if (isDuplicateFile) {
      dispatch(
        addToastNotification({ description: NotificationMessages.DUPLICATE_UPLOAD, type: 'Warning', timeout: 10000 }),
      );
      return;
    }

    const files = visualUnitFiles ? [...visualUnitFiles] : [];
    acceptedFiles.forEach((file) => files.push({ include: true, file, name: file.name }));
    onChangeVisualUnitFiles(files);
  };

  return (
    <div data-test-id="visual-unit-files">
      <p className="mb-2 sub-header-base text-neutral-950-opacity-60">Visual unit list</p>
      <div className="mb-1.5">
        <FileUpload
          acceptFileType={ACCEPTED_FILE_TYPE_XLS_ONLY}
          maxFileSize={FILE_SIZE_20MB}
          placeholder="Upload VU list"
          onFileChange={onFileChange}
          isDisabled={isReadOnly}
        />
      </div>
      {visualUnitFiles?.length ? (
        <div className="flex flex-col gap-2 mt-2">
          {visualUnitFiles?.map((item) => (
            <div key={item.name} aria-label={item.name} className="flex justify-between items-center">
              <Chip
                label={item.name}
                color={Color.PRIMARY}
                width={Width.FULL}
                truncateLabel={false}
                onRemove={() => onChangeVisualUnitFiles(visualUnitFiles.filter((file) => file.name !== item.name))}
                isDisabled={isReadOnly}
              />
              <IncludeExcludeToggle
                option={{ code: item.name, name: item.name, include: item.include }}
                onIncludeExcludeChange={(updatedItem) => {
                  onChangeVisualUnitFiles(
                    visualUnitFiles.map((file) =>
                      file.name === updatedItem.name ? { ...file, include: updatedItem.include } : file,
                    ),
                  );
                }}
                isAlwaysVisible
                isSelected={item.include !== undefined}
                isOptionNameSelectable={false}
                isHovered={false}
                isDeselectAllowed={false}
                isDisabled={isReadOnly}
              />
            </div>
          ))}
          {!isReadOnly && (
            <Button
              classNames="w-fit"
              label="Clear"
              color={ButtonColor.SECONDARY}
              onClick={() => onChangeVisualUnitFiles([])}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default VisualUnitFilesUpload;
